
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import TasksTable from "./TasksTable";
import PositAnalytics from "./PositAnalytics";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import PositPlusLogo from "@/components/shared/PositPlusLogo";
import { supabase } from "@/integrations/supabase/client";
import { TaskStatus } from "@/types/posit";
import ContentUploadDialog from "./ContentUploadDialog";
import StripeConnectOnboarding from "./StripeConnectOnboarding";

interface PositDashboardProps {
  positId: string;
  isPremium?: boolean;
}

const PremiumFeatureOverlay = () => {
  const navigate = useNavigate();
  
  return (
    <div className="absolute inset-0 backdrop-blur-sm bg-background/50 flex flex-col items-center justify-center gap-4 rounded-lg">
      <PositPlusLogo size="lg" />
      <p className="text-sm text-muted-foreground text-center max-w-[250px] mb-2">
        Your trial period has ended. Upgrade to Posit Plus to keep using this feature!
      </p>
      <Button 
        onClick={() => navigate("/#pricing")}
        className="bg-gradient-to-br from-primary via-primary-hover to-[#D946EF] text-white hover:opacity-90 transition-opacity"
      >
        Upgrade Now
      </Button>
    </div>
  );
};

const PositDashboard = ({ positId, isPremium = false }: PositDashboardProps) => {
  const { data: taskFlags = [] } = useQuery({
    queryKey: ["posit-task-flags", positId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posit_task_flags")
        .select("*")
        .eq("posit_id", positId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    },
  });

  const unprocessedFlags = taskFlags.filter(flag => !flag.processed);
  const hasUnprocessedTasks = unprocessedFlags.length > 0;

  const getTaskStatus = (flag: any): TaskStatus => {
    if (flag.status === 'open' || flag.status === 'closed' || flag.status === 'cancelled') {
      return flag.status;
    }
    return flag.processed ? 'closed' : 'open';
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight">Posit Dashboard</h2>
        {!isPremium && <PositPlusLogo size="md" className="scale-75" />}
      </div>
      <Tabs defaultValue="questions" className="w-full">
        <TabsList>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="tasks" className="relative">
            Tasks
            {hasUnprocessedTasks && (
              <Badge 
                variant="destructive" 
                className="ml-2 absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center"
              >
                {unprocessedFlags.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="questions" className="relative min-h-[300px] mt-4">
          <PositAnalytics positId={positId} />
          {!isPremium && <PremiumFeatureOverlay />}
        </TabsContent>

        <TabsContent value="analytics" className="relative min-h-[300px] mt-4">
          <div className="p-4 text-center text-muted-foreground">
            Analytics features coming soon!
          </div>
          {!isPremium && <PremiumFeatureOverlay />}
        </TabsContent>

        <TabsContent value="content" className="relative min-h-[300px] mt-4">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Paid Content</h3>
                <p className="text-sm text-muted-foreground">
                  Monetize your expertise by offering premium content
                </p>
              </div>
              <ContentUploadDialog 
                positId={positId} 
                onSuccess={() => window.location.reload()} 
              />
            </div>
            
            <StripeConnectOnboarding />
            
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-2">How it works</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Upload files with keywords that trigger recommendations</li>
                <li>• Set prices for your premium content</li>
                <li>• AI automatically suggests relevant content during conversations</li>
                <li>• Earn {isPremium ? '90%' : '80%'} of each sale</li>
              </ul>
            </div>
          </div>
          {!isPremium && <PremiumFeatureOverlay />}
        </TabsContent>
        
        <TabsContent value="tasks" className="relative min-h-[300px] mt-4">
          <TasksTable tasks={taskFlags.map(flag => ({
            id: flag.id,
            question: flag.question,
            status: getTaskStatus(flag),
            created_at: flag.created_at,
            updated_at: flag.created_at,
            posit_id: flag.posit_id,
            user_id: flag.user_id,
            notes: flag.context || '',
            response: flag.response || null
          }))} />
          {!isPremium && <PremiumFeatureOverlay />}
        </TabsContent>
      </Tabs>
    </>
  );
};

export default PositDashboard;
